import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { mapRotationQueryOptions } from "@/lib/client/apex";
import { DefaultImageCard } from "@/features/home/components/image-card";
import { Loading } from "@/features/shared/components/loading";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const NameMap = {
  worlds_edge_rotation: "世界边缘",
  canyonlands_rotation: "诸王峡谷",
  olympus_rotation: "奥林匹斯",
  storm_point_rotation: "风暴点",
  broken_moon_rotation: "破碎月亮",
  edistrict_rotation: "电力区",
};

const LocalMapImageMap: Record<string, string> = {
  Arena_Skulltown: "/images/map-rotation/Arena_Skulltown.webp",
  Broken_Moon: "/images/map-rotation/Broken_Moon.webp",
  "E-District": "/images/map-rotation/E-District.webp",
  Kings_Canyon: "/images/map-rotation/Kings_Canyon.webp",
  No_Map_Data: "/images/map-rotation/No_Map_Data.webp",
  Olympus: "/images/map-rotation/Olympus.webp",
  Worlds_Edge: "/images/map-rotation/Worlds_Edge.webp",
};

export function MapRotation() {
  const [tab, setTab] = useState<"ranked" | "pubs">("ranked");
  const { data, isLoading } = useQuery(mapRotationQueryOptions);

  const currentData = useMemo(() => {
    if (tab === "ranked") {
      return data?.ranked;
    }
    return data?.battle_royale;
  }, [data, tab]);

  const getMapUrl = (map: string) => {
    const mapName = map.split("/").at(-1)?.split(".")[0];
    if (!mapName) return map;
    return LocalMapImageMap[mapName] ?? map;
  };

  if (!data || isLoading || !currentData) return <Loading size={4} className="mx-auto" />;

  return (
    <DefaultImageCard image={getMapUrl(currentData.current.asset)}>
      <div className="flex flex-col gap-2 text-end text-white text-shadow-md">
        <Tabs
          defaultValue={tab}
          className="-mt-3 self-end"
          onValueChange={(value) => setTab(value as "ranked" | "pubs")}
        >
          <TabsList className="grid grid-cols-2 bg-transparent p-0 justify-self-end">
            <TabsTrigger value="ranked" className="text-white data-[state=active]:text-foreground">
              排位
            </TabsTrigger>
            <TabsTrigger value="pubs" className="text-white data-[state=active]:text-foreground">
              匹配
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <h1 className="text-3xl font-bold">
          <span className="text-xs text-white/60 font-mono mr-2">当前地图</span>
          {NameMap[currentData.current.code as keyof typeof NameMap] ?? currentData.current.map}
        </h1>

        <p className="text-sm text-white/60 font-mono">
          {format(currentData.current.start * 1000, "MM-dd HH:mm a")} -{" "}
          {format(currentData.current.end * 1000, "MM-dd HH:mm a")}
        </p>

        <h2 className="text-xl font-bold">
          <span className="text-xs text-white/60 font-mono mr-2">下一个</span>
          {NameMap[currentData.next.code as keyof typeof NameMap] ?? currentData.next.map}
        </h2>

        <p className="text-sm text-white/60 font-mono">
          开始时间 {format(currentData.next.start * 1000, "MM-dd HH:mm a")}
        </p>
      </div>
    </DefaultImageCard>
  );
}
